import { useState } from 'react';
import { ExternalLink, KeyRound, RotateCcw, Sparkles, Volume2, Wallet as WalletIcon } from 'lucide-react';

import { LoginArea } from '@/components/auth/LoginArea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useGround } from '@/hooks/useGround';
import { useGroundVoice } from '@/hooks/useGroundVoice';
import { useToast } from '@/hooks/useToast';
import { PROTOCOLS } from '@/lib/ground/protocols';
import { shortId } from '@/lib/ground/utils';

const PREVIEW_TEXT =
  'Welcome back. Take a slow breath in. {pause} And let it fall out, unhurried. {pause} You are here.';

function VoicePreview() {
  const voice = useGroundVoice();

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => (voice.status === 'playing' ? voice.stop() : voice.play(PREVIEW_TEXT))}
      >
        <Volume2 className="size-4" />
        {voice.status === 'playing' ? 'Stop preview' : 'Preview voice'}
      </Button>
      <Badge variant="secondary" className="font-normal">
        {voice.apiConfigured
          ? 'ElevenLabs ready'
          : voice.browserAvailable
            ? 'Browser voice (fallback)'
            : 'No voice available'}
      </Badge>
    </div>
  );
}

export function Wallet() {
  const {
    settings,
    updateSettings,
    identity,
    stats,
    purchases,
    ownsProtocol,
    resetStats,
    resetFoundations,
    resetPurchases,
  } = useGround();
  const { toast } = useToast();
  const [apiKeyVisible, setApiKeyVisible] = useState(false);

  const ownedCount = PROTOCOLS.filter(
    (protocol) => protocol.kind !== 'bundle' && ownsProtocol(protocol.id),
  ).length;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          Wallet &amp; Settings
        </h2>
        <p className="mt-3 text-muted-foreground">
          Wallet, voice, and identity settings are stored locally in your browser.
        </p>
      </div>

      <Card>
        <CardContent className="grid grid-cols-2 gap-4 py-5 sm:grid-cols-4">
          {[
            { label: 'Sessions', value: stats.sessions },
            { label: 'Sats earned', value: stats.satsEarned, demo: true },
            { label: 'Zaps sent', value: stats.zapsSent },
            { label: 'Protocols owned', value: ownedCount },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="font-display text-2xl font-semibold tabular-nums">
                {stat.value.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">
                {stat.label}
                {stat.demo && <span className="ml-1 opacity-60">(demo)</span>}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Lightning wallet */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 font-display text-lg">
            <WalletIcon className="size-5 text-primary" />
            Lightning wallet
          </CardTitle>
          <CardDescription>
            Zaps and protocol purchases run on Lightning. In demo mode everything is simulated
            locally.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 py-6">
          <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div>
              <div className="text-sm font-medium">Demo mode</div>
              <p className="text-xs text-muted-foreground">
                Simulate zaps, purchases, and rewards without spending real sats.
              </p>
            </div>
            <Switch
              checked={settings.demoMode}
              onCheckedChange={(checked) => updateSettings({ demoMode: checked })}
              aria-label="Toggle demo mode"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="nwc">NWC connection string</Label>
            <Input
              id="nwc"
              value={settings.nwcConnectionString}
              onChange={(event) => updateSettings({ nwcConnectionString: event.target.value.trim() })}
              placeholder="nostr+walletconnect://..."
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              Used for real NIP-57 zaps and payments in production. Copy it from Alby or any
              NWC-compatible wallet.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            <span>
              {purchases.length} purchase{purchases.length === 1 ? '' : 's'} recorded locally
            </span>
            {purchases.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto gap-1.5 text-muted-foreground"
                onClick={() => {
                  resetPurchases();
                  toast({ title: 'Purchases cleared', description: 'All protocols are locked again.' });
                }}
              >
                <RotateCcw className="size-3.5" />
                Clear purchases
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Voice */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 font-display text-lg">
            <Volume2 className="size-5 text-primary" />
            Voice
          </CardTitle>
          <CardDescription>
            Ground reads guided passages aloud. Add an ElevenLabs key for the full guided voice, or
            leave it blank to use your browser&rsquo;s built-in speech.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 py-6">
          <div className="space-y-2">
            <Label htmlFor="elevenlabs-key">ElevenLabs API key</Label>
            <div className="flex gap-2">
              <Input
                id="elevenlabs-key"
                type={apiKeyVisible ? 'text' : 'password'}
                value={settings.elevenLabsApiKey}
                onChange={(event) => updateSettings({ elevenLabsApiKey: event.target.value.trim() })}
                placeholder="sk_..."
                autoComplete="off"
                spellCheck={false}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => setApiKeyVisible((value) => !value)}
              >
                {apiKeyVisible ? 'Hide' : 'Show'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Stored only in this browser. Get one at{' '}
              <a
                href="https://elevenlabs.io/app/settings/api-keys"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-0.5 text-foreground underline underline-offset-2"
              >
                elevenlabs.io
                <ExternalLink className="size-3" />
              </a>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="elevenlabs-voice">Voice ID</Label>
            <Input
              id="elevenlabs-voice"
              value={settings.elevenLabsVoiceId}
              onChange={(event) => updateSettings({ elevenLabsVoiceId: event.target.value.trim() })}
              placeholder="e.g. 21m00Tcm4TlvDq8ikWAM"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              Find voice IDs in the ElevenLabs voice library. A calm narrator works best.
            </p>
          </div>

          <Separator />
          <VoicePreview />
        </CardContent>
      </Card>

      {/* Identity */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 font-display text-lg">
            <KeyRound className="size-5 text-primary" />
            Identity
          </CardTitle>
          <CardDescription>
            No login is required — Ground publishes with a local demo key. Sign in to post with your
            own Nostr identity.
          </CardDescription>
        </CardHeader>
        <CardContent className="py-6">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                {identity.isGuest ? 'Local demo identity' : 'Your Nostr account'}
                {identity.isGuest && (
                  <Badge variant="secondary" className="font-normal">
                    not logged in
                  </Badge>
                )}
              </div>
              <div className="mt-1 truncate font-mono text-xs text-muted-foreground">
                {shortId(identity.npub, 14)}
              </div>
            </div>
            <LoginArea className="max-w-60" />
          </div>
        </CardContent>
      </Card>

      {/* Data */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 font-display text-lg">
            <RotateCcw className="size-5 text-primary" />
            Your data
          </CardTitle>
          <CardDescription>
            {stats.sessions} sessions · {stats.steps} sovereignty steps · {stats.zapsSent} zaps sent
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3 py-6">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              resetStats();
              toast({ title: 'Stats reset', description: 'Your local counters are back to zero.' });
            }}
          >
            <RotateCcw className="size-4" />
            Reset stats
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              resetFoundations();
              toast({ title: 'Foundations reset', description: 'Your five-day progress was cleared.' });
            }}
          >
            <RotateCcw className="size-4" />
            Reset Foundations
          </Button>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2 pb-4 text-xs text-muted-foreground">
        <Sparkles className="size-3.5 text-primary" />
        Vibed with{' '}
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noreferrer noopener"
          className="text-foreground underline underline-offset-2"
        >
          Shakespeare
        </a>
      </div>
    </div>
  );
}
